import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth';
import { AcceptInvite } from './routes/AcceptInvite';
import { Activity } from './routes/Activity';
import { BidDetail } from './routes/BidDetail';
import { Bids } from './routes/Bids';
import { Bidders } from './routes/Bidders';
import { Download } from './routes/Download';
import { Login } from './routes/Login';
import { ProfileEditor } from './routes/ProfileEditor';
import { Profiles } from './routes/Profiles';
import { Targets } from './routes/Targets';

export function App(): JSX.Element {
  const { user, ready, signOut } = useAuth();

  // Gated on `ready` so an already-signed-in user never sees the login screen
  // flash while the refresh token is being exchanged.
  if (!ready) return <div className="main muted">Loading…</div>;

  return (
    <Routes>
      {/* Public on purpose: the invitee has no account yet, so this route must
          sit outside the auth gate that wraps everything else. */}
      <Route path="/invite/:token" element={<AcceptInvite />} />
      {/* Public for the same reason: the fixed link clients hand to bidders who
          have no account yet. */}
      <Route path="/download" element={<Download />} />
      <Route path="/*" element={user ? <Shell signOut={signOut} /> : <Login />} />
    </Routes>
  );
}

function Shell({ signOut }: { signOut: () => Promise<void> }): JSX.Element {
  return (
    <div className="app">
      <aside className="sidebar">
        <h2>BidManager</h2>
        <nav className="nav">
          <NavLink to="/bids">Bids</NavLink>
          <NavLink to="/profiles">Profiles</NavLink>
          <NavLink to="/activity">Activity</NavLink>
        </nav>
        <button
          className="ghost sm"
          style={{ marginTop: 20, width: '100%' }}
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </aside>

      <main className="main">
        <Routes>
          <Route path="/" element={<Navigate to="/bids" replace />} />
          <Route path="/bids" element={<Bids />} />
          <Route path="/bids/:bidId" element={<BidDetail />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/profiles/new" element={<ProfileEditor />} />
          <Route path="/profiles/:profileId" element={<ProfileEditor />} />
          <Route path="/profiles/:profileId/targets" element={<Targets />} />
          <Route path="/profiles/:profileId/bidders" element={<Bidders />} />
          <Route path="/activity" element={<Activity />} />
          {/* Instance administration moved to its own app (apps/admin) — a
              different origin, a different audience. Nothing links here. */}
          <Route path="*" element={<p className="muted">Not found.</p>} />
        </Routes>
      </main>
    </div>
  );
}
